/**
 * Eine Befund-Zeile (PV-052, architektur.md §9): Kopfzeile „Stufe · Titel · Projekt (Link, öffnet
 * das Popup — L5)“ (Feedback 1, F1), Satz, „seit …“, Aktionen,
 * aufklappbarer Verlauf. Einziges Bedienmuster — Mission Control, Popover und Projektdetail
 * nutzen genau diese Komponente.
 */
import type { BefundAnzeige } from '@projektverwaltung/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../../../lib/api.js';
import { formatIsoDate } from '../../../lib/format-date.js';
import { Badge } from '../badge.js';
import { HerkunftBadge } from '../herkunft-badge.js';
import { BefundAktionen } from './befund-aktionen.js';
import { BEFUNDE_QUERY_KEY, STUFE_TON, zurueckgestelltBisAktiv } from './befund-basis.js';
import { istEinfacherKlick, useProjektPopup } from './projekt-popup-kontext.js';

function datum(iso: string, sprache: string): string {
  return new Intl.DateTimeFormat(sprache, { dateStyle: 'medium' }).format(new Date(iso));
}

export function BefundZeile({
  befund,
  mitProjekt = true,
}: {
  befund: BefundAnzeige;
  /** Projektbezug (Nummer, Bezeichnung, Account als Link) — aus in der Hinweisleiste im Projektdetail. */
  mitProjekt?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const popup = useProjektPopup();
  const [verlaufOffen, setVerlaufOffen] = useState(false);
  // Zurückgestellt und noch nicht abgelaufen (Feedback 1, F2). Abgelaufen zählt wieder als aktiv
  // (dieselbe Regel wie Stapel/Übersicht, serverseitig `istAktiv`) und wird normal gezeigt.
  const zurueckgestelltBis = zurueckgestelltBisAktiv(befund);

  return (
    <li
      className="py-2.5 border-b last:border-b-0"
      // Gedimmt wie „besprochen“ in der Übergabeansicht (handover.tsx) — Aktionen bleiben nutzbar.
      style={{
        borderColor: 'var(--line-soft)',
        opacity: zurueckgestelltBis ? 0.6 : 1,
        transition: 'opacity 120ms ease-out',
      }}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Badge tone={STUFE_TON[befund.stufe]}>
          {t(`v2.befund.stufe_${befund.stufe}`, befund.stufe)}
        </Badge>
        <span className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>
          {befund.regel_titel}
        </span>
        <HerkunftBadge
          projectId={befund.herkunft_project_id}
          projectKey={befund.herkunft_project_key}
        />
        {zurueckgestelltBis && (
          <span
            className="inline-flex items-center px-1.5 py-0.5 rounded border text-[11px] font-medium"
            style={{
              borderColor: 'var(--ink-3)',
              color: 'var(--ink)',
              background: 'var(--surface-2)',
            }}
          >
            {t('v2.befund.zustand_zurueckgestellt', 'zurückgestellt bis {{datum}}', {
              datum: formatIsoDate(zurueckgestelltBis, i18n.language),
            })}
          </span>
        )}
        {mitProjekt && (
          <span aria-hidden="true" className="text-sm mx-1" style={{ color: 'var(--ink-3)' }}>
            |
          </span>
        )}
        {mitProjekt && (
          <Link
            to={`/projects/${befund.project_id}`}
            // Ein Link ist nativ ziehbar — auf einer Karte startete das sonst deren Drag.
            draggable={false}
            onDragStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              // Einfacher Klick → Popup, Liste bleibt stehen (L5). Strg/Cmd/Mittelklick → Browser.
              if (!popup || !istEinfacherKlick(e)) return;
              e.preventDefault();
              popup.oeffne(befund.project_id);
            }}
            // Gleiche Schriftgröße wie der Befund-Titel (Nutzerwunsch: nicht immer kleiner werden).
            className="inline-flex items-baseline gap-1.5 min-w-0 text-sm hover:underline"
            style={{ color: 'var(--ink)' }}
          >
            <span className="font-medium" style={{ fontFamily: 'var(--f-mono)' }}>
              {befund.project_key}
            </span>
            <span className="truncate">{befund.project_name}</span>
            <span className="truncate" style={{ color: 'var(--ink-2)' }}>
              · {befund.customer_name}
            </span>
          </Link>
        )}
        <span className="text-[11px] ml-auto" style={{ color: 'var(--ink-3)' }}>
          {befund.zustand === 'angefragt' && (
            <>{t('v2.befund.zustand_angefragt', 'angefragt')} · </>
          )}
          {t('v2.befund.seit', 'seit {{datum}}', {
            datum: datum(befund.erstmals_gesehen, i18n.language),
          })}
        </span>
      </div>
      <p className="text-sm mt-1" style={{ color: 'var(--ink-2)' }}>
        {befund.satz}
      </p>
      {befund.pl_antwort && (
        <p className="text-sm mt-1" style={{ color: 'var(--ink-2)' }}>
          {befund.pl_antwort.antwort === 'schliessen'
            ? t(
                'v2.befund.pl_antwort_schliessen',
                'PL-Antwort ({{name}}, {{am}}): kann geschlossen werden',
                {
                  name: befund.pl_antwort.von_name ?? '—',
                  am: datum(befund.pl_antwort.am, i18n.language),
                },
              )
            : t(
                'v2.befund.pl_antwort_verlaengern',
                'PL-Antwort ({{name}}, {{am}}): verlängern bis {{datum}}',
                {
                  name: befund.pl_antwort.von_name ?? '—',
                  am: datum(befund.pl_antwort.am, i18n.language),
                  datum: befund.pl_antwort.datum
                    ? formatIsoDate(befund.pl_antwort.datum, i18n.language)
                    : '—',
                },
              )}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2 mt-2">
        <BefundAktionen befund={befund} />
        <button
          type="button"
          aria-expanded={verlaufOffen}
          onClick={() => setVerlaufOffen((v) => !v)}
          className="text-xs ml-auto"
          style={{ color: 'var(--ink-3)' }}
        >
          {t('v2.befund.verlauf_zeigen', 'Verlauf')} {verlaufOffen ? '▴' : '▾'}
        </button>
      </div>
      {verlaufOffen && <BefundVerlaufListe befund={befund} />}
    </li>
  );
}

function BefundVerlaufListe({ befund }: { befund: BefundAnzeige }) {
  const { t, i18n } = useTranslation();
  const query = useQuery({
    queryKey: [...BEFUNDE_QUERY_KEY, 'verlauf', befund.befund_id],
    queryFn: () => api.getBefundVerlauf(befund.befund_id),
  });
  const zeit = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' }).format(
      new Date(iso),
    );
  const items = query.data?.items ?? [];
  return (
    <div className="mt-2 text-xs space-y-1" style={{ color: 'var(--ink-3)' }}>
      {query.isLoading ? (
        <p>{t('v2.befund.laedt', 'Lädt…')}</p>
      ) : items.length === 0 ? (
        <p>{t('v2.befund.verlauf_leer', 'Noch kein Verlauf.')}</p>
      ) : (
        <ol className="space-y-1">
          {items.map((v) => (
            <li key={v.verlauf_id}>
              <span style={{ fontFamily: 'var(--f-mono)' }}>{zeit(v.am)}</span> ·{' '}
              {v.ereignis === 'zurueckgestellt' && v.bis
                ? t('v2.befund.ereignis_zurueckgestellt_bis', 'zurückgestellt bis {{datum}}', {
                    datum: formatIsoDate(v.bis, i18n.language),
                  })
                : t(`v2.befund.ereignis_${v.ereignis}`, v.ereignis)}{' '}
              · {v.user_name ?? t('v2.befund.verlauf_system', 'Prüflauf')}
              {v.notiz ? ` — ${v.notiz}` : ''}
            </li>
          ))}
        </ol>
      )}
      <p>
        {t('v2.befund.zuletzt_bestaetigt', 'Zuletzt vom Prüflauf bestätigt: {{datum}}', {
          datum: zeit(befund.zuletzt_gesehen),
        })}
      </p>
    </div>
  );
}
